// Analytics utility for restaurant ordering system
// Supports Google Analytics 4 and custom analytics platforms

class AnalyticsManager {
  constructor() {
    this.isGoogleAnalyticsEnabled = typeof window !== 'undefined' && window.gtag;
    this.isCustomAnalyticsEnabled = typeof window !== 'undefined' && window.analytics;
    this.sessionId = this.generateSessionId();
    this.debugMode = process.env.NODE_ENV === 'development';
    this.verificationComplete = false;
    
    // Initialize verification
    this.initializeVerification();
  }

  // Enhanced verification for Google Analytics detection
  initializeVerification() {
    if (typeof window === 'undefined') return;
    
    // Wait for gtag to load
    const checkGtag = () => {
      if (window.gtag && window.dataLayer) {
        this.isGoogleAnalyticsEnabled = true;
        this.verificationComplete = true;
        
        // Send verification event
        this.trackVerificationEvent();
        
        console.log('✅ Google Analytics verification complete');
        
        // Add global flag for Google's crawler detection
        window.gtagVerified = true;
        window.pizzaInnAnalytics = {
          enabled: true,
          trackingId: 'G-FQWNYFDSCV',
          verified: true,
          timestamp: new Date().toISOString()
        };
        
      } else {
        // Retry after a short delay
        setTimeout(checkGtag, 100);
      }
    };
    
    // Start verification check
    setTimeout(checkGtag, 50);
  }

  // Track verification event for Google detection
  trackVerificationEvent() {
    if (!this.isGoogleAnalyticsEnabled) return;
    
    try {
      window.gtag('event', 'verification_complete', {
        event_category: 'Analytics',
        event_label: 'Google Analytics Loaded',
        verification_id: 'G-FQWNYFDSCV',
        spa_type: 'React',
        timestamp: new Date().toISOString(),
        page_location: window.location.href,
        page_title: document.title,
        user_agent: navigator.userAgent.substring(0, 100),
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language
      });
      
      // Additional tracking for verification
      window.gtag('config', 'G-FQWNYFDSCV', {
        page_title: document.title,
        page_location: window.location.href,
        send_page_view: true,
        debug_mode: this.debugMode
      });
      
    } catch (error) {
      console.error('Analytics verification error:', error);
    }
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Page view tracking
  trackPageView(page, customData = {}) {
    const data = {
      page_title: page,
      page_location: window.location.href,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      ...customData
    };

    // Google Analytics 4
    if (this.isGoogleAnalyticsEnabled) {
      window.gtag('event', 'page_view', data);
    }

    // Custom analytics
    if (this.isCustomAnalyticsEnabled) {
      window.analytics.track('Page Viewed', {
        page,
        ...data
      });
    }

    console.log('📊 Page View:', { page, ...data });
  }

  // Order flow events
  trackOrderEvent(eventName, orderData = {}) {
    const data = {
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      ...orderData
    };

    // Google Analytics 4
    if (this.isGoogleAnalyticsEnabled) {
      const gaEventMap = {
        'cart_viewed': 'view_cart',
        'checkout_started': 'begin_checkout',
        'order_completed': 'purchase',
        'item_added': 'add_to_cart',
        'item_removed': 'remove_from_cart'
      };

      const gaEvent = gaEventMap[eventName] || eventName;
      window.gtag('event', gaEvent, data);
    }

    // Custom analytics
    if (this.isCustomAnalyticsEnabled) {
      window.analytics.track(this.formatEventName(eventName), data);
    }

    console.log(`📊 Order Event: ${eventName}`, data);
  }

  // Error tracking
  trackError(errorType, errorDetails = {}) {
    const data = {
      error_type: errorType,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      page_location: window.location.href,
      ...errorDetails
    };

    // Google Analytics 4
    if (this.isGoogleAnalyticsEnabled) {
      window.gtag('event', 'exception', {
        description: errorType,
        fatal: false,
        ...data
      });
    }

    // Custom analytics
    if (this.isCustomAnalyticsEnabled) {
      window.analytics.track('Error Occurred', data);
    }

    console.error('📊 Error Tracked:', { errorType, ...data });
  }

  // User interaction tracking
  trackInteraction(action, element, data = {}) {
    const trackingData = {
      action,
      element,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      page_location: window.location.href,
      ...data
    };

    // Google Analytics 4
    if (this.isGoogleAnalyticsEnabled) {
      window.gtag('event', action, {
        event_category: 'User Interaction',
        event_label: element,
        ...trackingData
      });
    }

    // Custom analytics
    if (this.isCustomAnalyticsEnabled) {
      window.analytics.track('User Interaction', trackingData);
    }

    console.log('📊 Interaction:', trackingData);
  }

  // Form tracking
  trackFormEvent(formName, eventType, formData = {}) {
    const data = {
      form_name: formName,
      event_type: eventType,
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      ...formData
    };

    // Google Analytics 4
    if (this.isGoogleAnalyticsEnabled) {
      const gaEventMap = {
        'started': 'form_start',
        'completed': 'form_submit',
        'validation_error': 'form_validation_error'
      };

      const gaEvent = gaEventMap[eventType] || `form_${eventType}`;
      window.gtag('event', gaEvent, data);
    }

    // Custom analytics
    if (this.isCustomAnalyticsEnabled) {
      window.analytics.track(`Form ${this.formatEventName(eventType)}`, data);
    }

    console.log(`📊 Form ${eventType}:`, data);
  }

  // Performance tracking
  trackTiming(name, duration, category = 'Performance') {
    const data = {
      name,
      value: duration,
      category,
      session_id: this.sessionId,
      timestamp: new Date().toISOString()
    };

    // Google Analytics 4
    if (this.isGoogleAnalyticsEnabled) {
      window.gtag('event', 'timing_complete', {
        name,
        value: duration,
        event_category: category
      });
    }

    // Custom analytics
    if (this.isCustomAnalyticsEnabled) {
      window.analytics.track('Performance Timing', data);
    }

    console.log('📊 Performance:', data);
  }

  // Restaurant-specific events
  trackRestaurantEvent(eventName, restaurantData = {}) {
    const data = {
      session_id: this.sessionId,
      timestamp: new Date().toISOString(),
      ...restaurantData
    };

    const events = {
      menu_viewed: 'Menu Viewed',
      category_selected: 'Menu Category Selected',
      item_details_viewed: 'Menu Item Details Viewed',
      table_number_entered: 'Table Number Entered',
      special_instructions_added: 'Special Instructions Added',
      order_review_started: 'Order Review Started',
      order_confirmation_viewed: 'Order Confirmation Viewed',
      order_success_viewed: 'Order Success Viewed',
      reorder_initiated: 'Reorder Initiated'
    };

    const eventDisplayName = events[eventName] || this.formatEventName(eventName);

    // Google Analytics 4
    if (this.isGoogleAnalyticsEnabled) {
      window.gtag('event', eventName, {
        event_category: 'Restaurant Ordering',
        ...data
      });
    }

    // Custom analytics
    if (this.isCustomAnalyticsEnabled) {
      window.analytics.track(eventDisplayName, data);
    }

    console.log(`📊 Restaurant Event: ${eventDisplayName}`, data);
  }

  // Helper method to format event names
  formatEventName(eventName) {
    return eventName
      .replace(/_/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  }

  // Batch tracking for multiple events
  trackBatch(events) {
    events.forEach(event => {
      const { type, name, data } = event;
      
      switch (type) {
        case 'page':
          this.trackPageView(name, data);
          break;
        case 'order':
          this.trackOrderEvent(name, data);
          break;
        case 'error':
          this.trackError(name, data);
          break;
        case 'interaction':
          this.trackInteraction(name, data.element, data);
          break;
        case 'restaurant':
          this.trackRestaurantEvent(name, data);
          break;
        default:
          console.warn('Unknown event type:', type);
      }
    });
  }

  // Export session data for debugging
  getSessionInfo() {
    return {
      sessionId: this.sessionId,
      googleAnalyticsEnabled: this.isGoogleAnalyticsEnabled,
      customAnalyticsEnabled: this.isCustomAnalyticsEnabled,
      currentPage: window.location.href,
      timestamp: new Date().toISOString()
    };
  }
}

// Create singleton instance
const analytics = new AnalyticsManager();

// Export convenience methods
export const trackPageView = (page, data) => analytics.trackPageView(page, data);
export const trackOrderEvent = (event, data) => analytics.trackOrderEvent(event, data);
export const trackError = (error, details) => analytics.trackError(error, details);
export const trackInteraction = (action, element, data) => analytics.trackInteraction(action, element, data);
export const trackFormEvent = (form, event, data) => analytics.trackFormEvent(form, event, data);
export const trackTiming = (name, duration, category) => analytics.trackTiming(name, duration, category);
export const trackRestaurantEvent = (event, data) => analytics.trackRestaurantEvent(event, data);
export const trackBatch = (events) => analytics.trackBatch(events);
export const getSessionInfo = () => analytics.getSessionInfo();

export default analytics;
